import WebHttpClient from '~src/httpClient/WebHttpClient';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { AdminInsightsGetProjectIdRequest } from '~src/settings/IntegrationsSettings/serverApi/requests/AdminInsightsGetProjectIdRequest';
import { IAdminInsightsResult } from '~src/settings/IntegrationsSettings/serverApi/types/IAdminInsightsResult';

/**
 * Service that caches the Admin Insights project ID for the current site.
 * Used to identify whether content belongs to the Admin Insights project
 * without making repeated API calls on every content open.
 *
 * Lifecycle:
 *  - On first call, fetches from backend and caches.
 *  - Subsequent calls return the cached value (per site session).
 *  - Returns `-1` if Admin Insights is not enabled or the API call fails.
 *
 * Thread safety: only one inflight request at a time (deduped via the
 * pending promise).
 */

let cachedProjectId: number | undefined;
let pendingRequest: Promise<number> | undefined;

/**
 * Returns the Admin Insights project ID for the current site.
 * Returns -1 if Admin Insights is not enabled.
 * Caches the result for the session lifetime.
 */
export async function getAdminInsightsProjectId(): Promise<number> {
  if (cachedProjectId !== undefined) {
    return cachedProjectId;
  }

  if (pendingRequest) {
    return pendingRequest;
  }

  pendingRequest = fetchAdminInsightsProjectId();

  try {
    cachedProjectId = await pendingRequest;
    return cachedProjectId;
  } finally {
    pendingRequest = undefined;
  }
}

/**
 * Check if Admin Insights is enabled on the current site.
 * Uses the cached site metadata flag (zero API calls).
 */
export function isAdminInsightsEnabledOnSite(): boolean {
  try {
    return getSessionManagementServiceInstance().isAdminDataSourceEnabled();
  } catch {
    return false;
  }
}

/**
 * Check if a given content's project ID matches the Admin Insights project.
 * @param contentProjectId - The project.id from the workbook/datasource detail info.
 * @returns true if this content belongs to the Admin Insights project.
 */
export async function isAdminInsightsContent(contentProjectId: string | undefined): Promise<boolean> {
  if (!contentProjectId) {
    return false;
  }

  // Quick check: if Admin Insights isn't enabled on this site, skip the API call
  if (!isAdminInsightsEnabledOnSite()) {
    return false;
  }

  const adminProjectId = await getAdminInsightsProjectId();

  // -1 means Admin Insights is not enabled (no project exists)
  if (adminProjectId === -1) {
    return false;
  }

  // Compare: content's project ID (string) === admin insights project ID (number → string)
  return contentProjectId === String(adminProjectId);
}

/**
 * Reset the cache (for testing or site switches).
 */
export function resetAdminInsightsCache(): void {
  cachedProjectId = undefined;
  pendingRequest = undefined;
}

// --- Private ---

async function fetchAdminInsightsProjectId(): Promise<number> {
  try {
    const request = new AdminInsightsGetProjectIdRequest();
    const result: IAdminInsightsResult = await WebHttpClient.sendRequest(request);
    return result.projectId;
  } catch {
    // If the API call fails, treat as "not enabled" rather than crashing the content load
    return -1;
  }
}
